import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { EmptyState } from '@/components/shared/empty-state';
import { FormField } from '@/components/shared/form-field';
import { Spinner, TableSkeleton } from '@/components/shared/loading';
import { Pagination, useCursorPages } from '@/components/shared/pagination';
import { SectionCard } from '@/components/shared/section-card';
import { SimpleSelect } from '@/components/shared/simple-select';
import { outcomeTone, StatusBadge, ToneBadge } from '@/components/shared/tone-badge';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { Message, Page, Send, SendDetail } from '@/lib/types';
import { useAppCtx } from './app-layout';

const SOURCE_LABEL: Record<Send['source'], string> = { server: 'server', admin_test: 'test', admin_broadcast: 'dashboard' };

export function Sends() {
  const { app } = useAppCtx();
  const [status, setStatus] = useState('');
  const [user, setUser] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const pages = useCursorPages({
    queryKey: ['sends', app.id, status, user],
    fetchPage: (before, limit) => api<Page<Message>>(`/admin/v1/apps/${app.id}/sends`, { query: { status, external_user_id: user, before, limit } }),
    refetchInterval: 10_000,
  });
  const list = pages.query;
  const rows = pages.rows;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <FormField label="Status" className="w-40">
          <SimpleSelect value={status} onValueChange={setStatus} options={[{ value: '', label: 'All' }, { value: 'queued', label: 'Queued' }, { value: 'accepted', label: 'Accepted' }, { value: 'failed', label: 'Failed' }]} />
        </FormField>
        <FormField label="External User ID" htmlFor="user" className="w-64"><Input id="user" value={user} onChange={(e) => setUser(e.target.value)} placeholder="e.g. user_042" /></FormField>
      </div>
      <SectionCard flush className={list.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {list.isLoading ? <TableSkeleton /> : rows.length === 0 ? <EmptyState icon={History} title="No notifications match" /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Status</TableHead><TableHead>Title</TableHead><TableHead>Source</TableHead><TableHead className="whitespace-nowrap">External User ID</TableHead>
                <TableHead>Targets</TableHead><TableHead>Accepted</TableHead><TableHead>Failed</TableHead><TableHead>Clicks</TableHead>
                <TableHead>Error</TableHead><TableHead className="pr-5">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => setOpen(s.id)}>
                  <TableCell className="pl-5"><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="max-w-[14rem] truncate font-medium">{s.title}</TableCell>
                  <TableCell><ToneBadge>{SOURCE_LABEL[s.source]}</ToneBadge></TableCell>
                  <TableCell className="font-mono text-xs">{s.external_user_id ?? '—'}</TableCell>
                  <TableCell className="tabular-nums">{s.target_count}</TableCell>
                  <TableCell className="tabular-nums">{s.accepted_count}</TableCell>
                  <TableCell className="tabular-nums">{s.failed_count}</TableCell>
                  <TableCell className="tabular-nums">{s.click_count}</TableCell>
                  <TableCell className="font-mono text-xs text-destructive">{s.error_code ?? ''}</TableCell>
                  <TableCell className="pr-5 text-muted-foreground">{fmtDate(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination p={pages} noun="notifications" />
      </SectionCard>
      <SendSheet appId={app.id} id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function SendSheet({ appId, id, onClose }: { appId: string; id: string | null; onClose(): void }) {
  const q = useQuery({ queryKey: ['send', appId, id], enabled: !!id, queryFn: () => api<SendDetail>(`/admin/v1/apps/${appId}/sends/${id}`) });
  const s = q.data;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full gap-0 overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="border-b">
          <SheetTitle>Notification detail</SheetTitle>
          <SheetDescription>Delivery outcome for every targeted device.</SheetDescription>
        </SheetHeader>
        {!s ? <Spinner /> : (
          <div className="space-y-6 p-4">
            <div className="space-y-1 rounded-lg border bg-muted/40 p-4">
              <p className="font-medium">{s.title}</p>
              {s.body && <p className="text-sm text-muted-foreground">{s.body}</p>}
            </div>
            <dl className="grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2.5 text-sm">
              <Detail k="Status"><StatusBadge status={s.status} /></Detail>
              <Detail k="Message ID"><span className="font-mono text-xs break-all">{s.id}</span></Detail>
              <Detail k="Targets"><span className="tabular-nums">{s.target_count}</span></Detail>
              {s.batch_count > 1 && (
                <Detail k="Batches">
                  <span className="tabular-nums">{s.batch_count}</span>
                  <span className="text-muted-foreground"> · sent in groups of up to 20 devices</span>
                </Detail>
              )}
              <Detail k="Correlation ID"><span className="break-all font-mono text-xs">{s.correlation_id}</span></Detail>
              <Detail k="Idempotency key"><span className="font-mono text-xs break-all">{s.idempotency_key ?? '—'}</span></Detail>
              <Detail k="Created">{fmtDate(s.created_at)}</Detail>
              <Detail k="Completed">{fmtDate(s.completed_at)}</Detail>
            </dl>
            <SectionCard title="Outcomes per device" flush>
              {s.outcomes_truncated && (
                <p className="border-b px-4 py-2 text-xs text-muted-foreground">Showing the first {s.outcomes.length.toLocaleString()} device outcomes.</p>
              )}
              {s.outcomes.length === 0 ? <EmptyState title={s.status === 'queued' ? 'Still sending…' : 'No outcomes recorded'} /> : (
                // Fixed columns so the table always fits the panel: no sideways scroll, long errors are truncated.
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28 pl-4">Event</TableHead><TableHead className="w-20">Platform</TableHead><TableHead className="w-28">Installation</TableHead>
                      <TableHead>Error</TableHead><TableHead className="w-24 pr-4 text-right">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {s.outcomes.map((o, i) => (
                      <TableRow key={i}>
                        <TableCell className="pl-4"><ToneBadge tone={outcomeTone(o.event_type)} dot>{o.event_type}</ToneBadge></TableCell>
                        <TableCell>{o.platform === 'ios' ? 'iOS' : o.platform === 'android' ? 'Android' : '—'}</TableCell>
                        <TableCell className="font-mono text-xs" title={o.installation_id ?? undefined}>{o.installation_id?.slice(0, 8) ?? '—'}</TableCell>
                        <TableCell className="truncate font-mono text-xs" title={o.error_code ?? undefined}>{o.error_code ?? ''}</TableCell>
                        {/* The day is in Created above; the full timestamp is on hover. */}
                        <TableCell className="pr-4 text-right text-muted-foreground tabular-nums" title={fmtDate(o.time)}>{fmtTime(o.time)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionCard>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Detail = ({ k, children }: { k: string; children: ReactNode }) => (<><dt className="text-muted-foreground">{k}</dt><dd className="min-w-0">{children}</dd></>);

const fmtTime = (s: string) => new Date(s).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
